import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {SvgIconEl} from 'common/components/svg-icon';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {Directions} from 'common/components/drop-menu/types';
import {ReportName} from 'common/report/constants';
import {useReportConsistency} from 'common/report/util/feature-sampling';

import type {QueryEntrySection} from '../index';
import {DROP_POSITION} from '../types';

export function AddEntryHeader({el}: {el: QueryEntrySection}) {
  const label = el.attr(`section-title`);
  const isAddingDisabled = el.helpers.isAddingDisabled();
  // If there is only one entry that is an empty placeholder (intial empty state), do not disable select
  const isSelectDisabled =
    el.helpers.getEntries().length === 1 && el.isEmptyPlaceholder(0, el.getEntry(0)) ? false : isAddingDisabled;
  const typeItems = el.helpers.getAddListTypeItems();
  const tooltipAttrs = el.helpers.getAddEntryButtonTooltipAttrs();
  const hasConsolidatedMetricsFF = el.hasConsolidatedMetricsUI();
  const singleEntryType = typeItems.length === 1 && typeItems[0].value;
  const dropAreas = el.helpers.allowDropAreas(el.state.draggingOverStepIndex);
  const canDrop = dropAreas.has(DROP_POSITION.HEADER);
  const canAlterEntries = !el.attr(`readonly`) && (el.helpers.isDuplicateSupported() || el.helpers.canDeleteEntry());
  const isAddingSupported = !el.attr(`disable-add-entry`) && (el.attr(`row-limit`) > 0 ? canAlterEntries : true);
  const shouldRenderSelectInMetrics =
    el.attr(`report-name`) === `insights` && hasConsolidatedMetricsFF && label === `Metrics`;
  return (
    <>
      {canDrop && (
        <div
          sel='.header-drop-zone.drop-zone'
          on={{
            dragleave: () => el.update({draggingOverStepIndex: null}),
            drop: (ev) => canDrop && el.helpers.onEntryStepDrop(ev, 0),
            dragover: (ev) => el.helpers.onEntryStepDragOver(ev, 0),
          }}
        />
      )}
      <div
        sel='.mp-query-entry-section-header'
        class={{
          'is-empty': el.helpers.getEntries().length === 0,
          'is-sticky': el.helpers.isStickyHeader(),
        }}
        hook={{
          insert: el.helpers.isStickyHeader() && ((vnode) => el.helpers.onInsertStickyHeader(vnode.elm as HTMLElement)),
        }}
      >
        {/* TODO(CC): Display header label for retention report as well once retention report implements insights app */}
        {!singleEntryType && useReportConsistency() && el.attr(`report-name`) === ReportName.Funnels ? (
          <div sel='.mp-query-entry-section-header-label'>{label}</div>
        ) : singleEntryType && !shouldRenderSelectInMetrics ? (
          <ButtonEl
            attrs={{
              disabled: isAddingDisabled,
              theme: ButtonTheme.QueryBuilder,
              elref: `newEntryButton`,
              pressed: el.state.draggingOverStepIndex === 0 && canDrop,
              tooltip: tooltipAttrs,
              size: ButtonSize.Large,
              'experimental-positioning': true,
            }}
            class={{
              'is-open': el.state.isTypeSelectOpen,
            }}
            on={{
              click: !isAddingDisabled && (() => el.addEntryRow(singleEntryType)),
            }}
          >
            {label}
            <div sel='.mp-query-entry-section-query-header-settings'>
              {isAddingSupported && <SvgIconEl attrs={{icon: `plus`}} />}
            </div>
          </ButtonEl>
        ) : (
          <SelectEl
            attrs={{
              elref: `typeSelect`,
              disabled: isSelectDisabled,
              'always-show-caret': true,
              icon: {leftIcon: ``, rightIcon: `plus`},
              'menu-width': el.attr(`add-entry-menu-width`),
              'menu-alignment': Directions.Right,
              sections: [{items: typeItems}],
              theme: SelectTheme.Tertiary,
              tooltip: tooltipAttrs,
              label: shouldRenderSelectInMetrics ? `Metrics` : ``, // only if feature flag is on.
              'experimental-positioning': true,
            }}
            class={{
              'is-open': el.state.isTypeSelectOpen,
            }}
            on={{
              change:
                !isSelectDisabled && ((ev: CustomEvent) => el.addEntryRow(ev.detail.item.value, {typeSelect: true})),
              menuChange: el.helpers.onAddTypeSelectMenuChange,
            }}
          />
        )}
        <slot attrs={{name: `header-items`}} />
      </div>
    </>
  );
}
